import React, { useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { ExternalLink, Download, FileText, ChevronLeft, ChevronRight } from "lucide-react";

// Serve the worker from the CDN with a version that matches the installed pdfjs-dist.
// This avoids CRA build-time worker plumbing and works everywhere.
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * PdfViewer — renders a PDF using PDF.js (canvas-based), NOT the browser's
 * built-in PDF plugin. This bypasses "This page has been blocked by Chrome"
 * errors that happen when the PDF plugin refuses to render inside iframes /
 * sandboxed contexts (e.g. inside the Emergent preview shell).
 *
 * Props:
 *   fileUrl:  string — the PDF URL
 *   filename: string — used for the Download button
 */
export default function PdfViewer({ fileUrl, filename }) {
  const [numPages, setNumPages] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);  // 0-based
  const [containerWidth, setContainerWidth] = useState(720);
  const [loadError, setLoadError] = useState(null);
  const wrapRef = React.useRef(null);

  useEffect(() => {
    if (!wrapRef.current) return;
    const el = wrapRef.current;
    const measure = () => setContainerWidth(Math.max(320, Math.min(el.clientWidth - 24, 1000)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Reset when file changes
  useEffect(() => {
    setPageIndex(0);
    setNumPages(0);
    setLoadError(null);
  }, [fileUrl]);

  const total = numPages || 0;
  const canPrev = pageIndex > 0;
  const canNext = pageIndex < total - 1;

  return (
    <div ref={wrapRef} className="flex-1 flex flex-col bg-white" data-testid="pdf-viewer">
      {/* Page controls */}
      {total > 0 && !loadError && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-[#e4ded2] bg-[#fbf8f2]" data-testid="pdf-controls">
          <button
            onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
            disabled={!canPrev}
            data-testid="pdf-prev"
            className="flex items-center gap-1 text-xs uppercase tracking-widest text-[#1a1612] disabled:opacity-30 hover:text-[#6f2a30]"
          >
            <ChevronLeft className="h-4 w-4" /> Prev
          </button>
          <div className="font-mono text-xs text-[#6b6258]" data-testid="pdf-page-indicator">
            Page {pageIndex + 1} of {total}
          </div>
          <button
            onClick={() => setPageIndex((i) => Math.min(total - 1, i + 1))}
            disabled={!canNext}
            data-testid="pdf-next"
            className="flex items-center gap-1 text-xs uppercase tracking-widest text-[#1a1612] disabled:opacity-30 hover:text-[#6f2a30]"
          >
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex-1 overflow-auto flex justify-center items-start py-4">
        {loadError ? (
          <div className="grid place-items-center p-8 text-center max-w-sm">
            <FileText className="h-10 w-10 text-[#9c8f7e] mx-auto mb-4" strokeWidth={1.2} />
            <div className="text-sm text-[#1a1612] font-semibold mb-2">Couldn&apos;t render this PDF inline.</div>
            <div className="text-xs text-[#6b6258] leading-relaxed mb-5">
              This can happen with password-protected or corrupted PDFs. Try opening it in a new tab or downloading it.
            </div>
            <div className="flex items-center justify-center gap-2">
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#6f2a30] text-[#f1eee7] text-xs uppercase tracking-widest font-medium rounded-sm hover:bg-[#582227]"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Open in new tab
              </a>
              <a
                href={fileUrl}
                download={filename}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-[#d8ceb8] text-[#1a1612] text-xs uppercase tracking-widest font-medium rounded-sm hover:bg-[#f1eee7]"
              >
                <Download className="h-3.5 w-3.5" /> Download
              </a>
            </div>
          </div>
        ) : (
          <Document
            file={fileUrl}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            onLoadError={(err) => { console.error("PDF load error", err); setLoadError(err?.message || String(err)); }}
            loading={<div className="text-sm text-[#6b6258] py-16">Loading PDF…</div>}
            error={<div className="text-sm text-[#6f2a30] py-16">Failed to load PDF.</div>}
          >
            {total > 0 && (
              <Page
                pageNumber={pageIndex + 1}
                width={containerWidth}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            )}
          </Document>
        )}
      </div>
    </div>
  );
}
